import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import QRCode from 'react-qr-code';
import { supabase } from '@/lib/supabase';
import { mensagemAmigavel } from '@/lib/mensagemAmigavel';
import { basePathSite } from '@/pages/assistente-evento/utils';
import '@/pages/rifa-imprimir/imprimir.css';

interface EventoCartaz {
  id: number;
  nome: string;
  slug: string;
  imagem_mobile_url: string | null;
  imagem_desktop_url: string | null;
}

// Cartaz das fichas (A4, imprimível): capa + nome + QR do link das fichas.
export default function CartazFichasPage() {
  const { eventoId } = useParams<{ eventoId: string }>();
  const [evento, setEvento] = useState<EventoCartaz | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    let ativo = true;
    const idNum = Number(eventoId);
    if (!Number.isFinite(idNum)) {
      setErro('Evento inválido.');
      setCarregando(false);
      return;
    }
    supabase
      .from('eventos')
      .select('id, nome, slug, imagem_mobile_url, imagem_desktop_url')
      .eq('id', idNum)
      .maybeSingle()
      .then(({ data, error }) => {
        if (!ativo) return;
        if (error) {
          setErro(mensagemAmigavel(error));
          setCarregando(false);
          return;
        }
        if (!data) {
          setErro('Evento não encontrado.');
          setCarregando(false);
          return;
        }
        setEvento(data as EventoCartaz);
        setCarregando(false);
      });
    return () => {
      ativo = false;
    };
  }, [eventoId]);

  const capa = evento?.imagem_mobile_url || evento?.imagem_desktop_url;
  const linkFichas = evento
    ? `${window.location.origin}${basePathSite()}/e/${evento.slug}/fichas`
    : '';

  if (carregando) {
    return (
      <div className="min-h-screen bg-background-100">
        <div className="mx-auto w-full max-w-[880px] px-4 py-8">
          <div className="h-8 w-64 animate-pulse rounded bg-secondary-100" />
          <div className="mt-6 h-[500px] w-full animate-pulse rounded-lg bg-secondary-100" />
        </div>
      </div>
    );
  }

  if (erro || !evento) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background-100 px-4 py-8">
        <div className="w-full max-w-[420px] overflow-hidden rounded-lg border border-secondary-200 bg-white">
          <div className="h-[10px] w-full bg-primary-500" />
          <div className="p-8 text-center">
            <h1 className="text-xl font-medium text-foreground-950">Não foi possível carregar</h1>
            <p className="mt-2 text-sm text-foreground-600">{erro}</p>
            <Link
              to="/meus-eventos"
              className="mt-6 inline-block w-full cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-white transition-colors hover:bg-primary-600"
            >
              Voltar aos meus eventos
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pagina-imprimir min-h-screen bg-background-100">
      {/* Barra superior — fora da impressão */}
      <div className="no-print border-b border-secondary-200 bg-white">
        <div className="mx-auto flex w-full max-w-[880px] flex-col gap-4 px-4 py-4 md:flex-row md:items-end md:justify-between">
          <div>
            <Link
              to="/meus-eventos"
              className="inline-flex cursor-pointer items-center gap-1 whitespace-nowrap text-sm font-medium text-secondary-600 transition-colors hover:text-foreground-800"
            >
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-arrow-left-line" />
              </span>
              Voltar
            </Link>
            <h1 className="mt-1 text-[26px] font-normal text-foreground-950">
              Cartaz das fichas
            </h1>
            <p className="mt-0.5 text-sm text-secondary-500">
              Imprima e pendure no evento. As pessoas apontam a câmera e compram.
            </p>
          </div>
          <button
            type="button"
            onClick={() => window.print()}
            className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-white transition-colors hover:bg-primary-600"
          >
            <span className="flex h-4 w-4 items-center justify-center">
              <i className="ri-printer-line" />
            </span>
            Imprimir
          </button>
        </div>
      </div>

      {/* Folha A4 */}
      <div className="mx-auto w-full max-w-[880px] px-4 py-8">
        <div className="folha">
          <div className="folha-interna items-center justify-center gap-6 text-center">
            {capa && (
              <div className="mx-auto w-full max-w-[150mm] overflow-hidden rounded-xl">
                <img
                  src={capa}
                  alt={evento.nome}
                  title={`Cartaz das fichas de ${evento.nome}`}
                  className="h-auto w-full object-contain"
                  decoding="async"
                />
              </div>
            )}

            <h2 className="text-[34px] font-black uppercase leading-tight text-foreground-950">
              {evento.nome}
            </h2>

            <div className="border-2 border-black p-3">
              <QRCode value={linkFichas} size={220} level="M" />
            </div>

            <p className="text-[22px] font-bold uppercase tracking-wide text-foreground-950">
              Aponte a câmera e compre suas fichas
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}